import CharacterClassIcon from './CharacterClassIcon'
import CharacterLink from './CharacterLink'
import { cn } from '@/lib/utils'
import { classColours } from 'app/frontend/types/custom/CharacterClass'
import BlizzardIcon from './BlizzardIcon'
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@/components/ui/tooltip'
import CharacterRoleIcon from './CharacterRoleIcon'
import { useTeam } from 'app/frontend/base/config/context/team'

type SpecDetail = NonNullable<wowaudit.TeamMembership['specDetails']>

type SplitSpecIconProps = {
  specs: SpecDetail[]
  size: 'tiny' | 'small' | 'medium' | 'large'
  className?: string
}

const sizeClasses: Record<SplitSpecIconProps['size'], string> = {
  tiny: 'w-3 h-3 min-w-3 min-h-3',
  small: 'w-4 h-4 min-w-4 min-h-4',
  medium: 'w-6 h-6 min-w-6 min-h-6',
  large: 'w-8 h-8 min-w-8 min-h-8',
}

const SplitSpecIcon = ({ specs, size, className }: SplitSpecIconProps) => {
  const stripeCount = specs.length
  const offset = 15
  const gap = 1

  return (
    <div
      className={cn(
        'relative overflow-hidden rounded-md border border-[#666]',
        sizeClasses[size],
        className,
      )}
    >
      {specs.map((spec, index) => {
        const stripeWidth =
          (100 + offset - (stripeCount - 1) * gap) / stripeCount
        const bottomLeft = index * (stripeWidth + gap) - offset
        const bottomRight = bottomLeft + stripeWidth
        const topLeft = bottomLeft + offset
        const topRight = bottomRight + offset

        return (
          <div
            key={`${spec.icon}-${index}`}
            style={{
              position: 'absolute',
              inset: 0,
              backgroundImage: `url("https://render.worldofwarcraft.com/us/icons/36/${spec.icon}.jpg")`,
              backgroundPosition: 'center',
              backgroundSize: 'cover',
              clipPath: `polygon(${topLeft}% 0, ${topRight}% 0, ${bottomRight}% 100%, ${bottomLeft}% 100%)`,
            }}
          />
        )
      })}
    </div>
  )
}

export type RankColorPresentation =
  | 'replace_class_color'
  | 'replace_class_icon'
  | 'show_as_letter'
  | 'show_as_orb'
  | 'replace_icon_border'
  | 'hidden'

export const getTeamMemberNameColumnSize = (
  team: Pick<wowaudit.Team, 'settings' | 'members'> | undefined,
) => {
  const rankPresentation = team?.settings?.rankColorPresentation?.value as
    | RankColorPresentation
    | undefined
  const hasRankAdornment =
    rankPresentation === 'show_as_orb' || rankPresentation === 'show_as_letter'
  const hasRankColor = (team?.members ?? []).some(
    (member) => !!member.teamRank?.color,
  )

  return hasRankAdornment && hasRankColor ? 215 : 175
}

type Props = {
  member?: wowaudit.TeamMembership
  characterReference?: wowaudit.CharacterReference
  compact?: boolean
  customSpecDetails?: SpecDetail[]
  withTooltip?: boolean
  iconType?: 'class' | 'role' | 'none' | 'both'
  rankColorPresentationOverride?: RankColorPresentation
  disableNavigation?: boolean
}

const TeamMemberLink = ({
  member,
  characterReference,
  compact,
  customSpecDetails,
  withTooltip,
  iconType = 'class',
  rankColorPresentationOverride,
  disableNavigation,
}: Props) => {
  if (!member && !characterReference) return null
  const { team } = useTeam()
  const character = (member?.characterReference ??
    characterReference) as wowaudit.CharacterReference

  const specDetailsList = (
    customSpecDetails ?? (member?.specDetails ? [member.specDetails] : [])
  ).filter((spec) => spec.name)
  const rankColor = member?.teamRank?.color
  const rankName = member?.teamRank?.name?.trim()
  const rankPresentation = rankColorPresentationOverride
    ? rankColorPresentationOverride
    : team?.settings?.rankColorPresentation?.value
  const hasRankColor = !!rankColor
  const rankLetter = (member?.teamRank?.name || character.name || '?')
    .trim()
    .charAt(0)
    .toUpperCase()
  const showBorderOnClassIcon =
    hasRankColor && rankPresentation === 'replace_icon_border'

  const rankBadge = hasRankColor ? (
    <span
      className={cn(
        'inline-flex items-center justify-center font-semibold border text-zinc-950 cursor-default',
        compact
          ? 'h-4 min-w-4 rounded text-[10px] px-1'
          : 'h-5 min-w-5 rounded-md text-xs px-1',
      )}
      style={{ backgroundColor: rankColor ?? undefined }}
    >
      {rankLetter}
    </span>
  ) : null

  const rankOrb = hasRankColor ? (
    <span
      className={cn(
        'inline-flex rounded-full border',
        compact ? 'h-2.5 w-2.5 min-w-2.5 min-h-2.5' : 'h-3 w-3 min-w-3 min-h-3',
      )}
      style={{
        backgroundColor: rankColor ?? undefined,
      }}
    />
  ) : null

  const iconSize = compact ? 'small' : 'medium'
  const classIcon =
    specDetailsList.length > 1 ? (
      <SplitSpecIcon
        specs={specDetailsList}
        size={iconSize}
        className={showBorderOnClassIcon ? 'border-transparent' : undefined}
      />
    ) : specDetailsList.length === 1 ? (
      <BlizzardIcon
        size={iconSize}
        iconName={specDetailsList[0].icon}
        className={showBorderOnClassIcon ? 'border-transparent' : undefined}
      />
    ) : (
      <CharacterClassIcon
        size={iconSize}
        characterClass={character.characterClass}
        className={showBorderOnClassIcon ? 'border-transparent' : undefined}
      />
    )
  const classIconWithBorder = showBorderOnClassIcon ? (
    <span
      className={cn(
        'inline-flex rounded-md p-px',
        compact ? 'rounded-sm' : 'rounded-md',
      )}
      style={{ backgroundColor: rankColor ?? undefined }}
    >
      {classIcon}
    </span>
  ) : (
    classIcon
  )
  const classIconPresentation =
    hasRankColor && rankPresentation === 'replace_class_icon'
      ? rankBadge
      : classIconWithBorder

  const roleIcon = member ? (
    <CharacterRoleIcon role={member.role} size={compact ? 'small' : 'medium'} />
  ) : null

  const icon =
    iconType === 'class' ? (
      classIconPresentation
    ) : iconType === 'role' ? (
      roleIcon
    ) : iconType === 'both' ? (
      <>
        {roleIcon}
        {classIconPresentation}
      </>
    ) : null

  const showRankOrb = hasRankColor && rankPresentation === 'show_as_orb'
  const showRankLetter = hasRankColor && rankPresentation === 'show_as_letter'
  const replaceClassColor =
    hasRankColor && rankPresentation === 'replace_class_color'
  const showIconTooltip =
    !!icon && (withTooltip || (showBorderOnClassIcon && !!rankName))

  return (
    <div
      className={cn('flex items-center min-w-0', compact ? 'gap-1.5' : 'gap-2')}
    >
      {icon ? (
        showIconTooltip ? (
          <Tooltip>
            <TooltipTrigger asChild>
              <span
                className={cn(
                  'inline-flex items-center shrink-0',
                  compact ? 'gap-1.5' : 'gap-2',
                )}
              >
                {icon}
              </span>
            </TooltipTrigger>
            <TooltipContent className='text-center'>
              <div>
                {specDetailsList.length > 0
                  ? `${specDetailsList.map((s) => s.name).join(' / ')} ${
                      character.characterClass
                    }`
                  : character.characterClass}
              </div>
              {showBorderOnClassIcon && rankName ? (
                <div className='text-muted-foreground text-xs'>{rankName}</div>
              ) : null}
            </TooltipContent>
          </Tooltip>
        ) : (
          <span
            className={cn(
              'inline-flex items-center shrink-0',
              compact ? 'gap-1.5' : 'gap-2',
            )}
          >
            {icon}
          </span>
        )
      ) : null}
      {showRankOrb ? (
        <Tooltip>
          <TooltipTrigger asChild>
            <span>{rankOrb}</span>
          </TooltipTrigger>
          <TooltipContent>{rankName || 'Rank'}</TooltipContent>
        </Tooltip>
      ) : null}
      {showRankLetter ? (
        <Tooltip>
          <TooltipTrigger asChild>
            <span>{rankBadge}</span>
          </TooltipTrigger>
          <TooltipContent>{rankName || 'Rank'}</TooltipContent>
        </Tooltip>
      ) : null}
      {disableNavigation ? (
        <span
          className={cn(
            'truncate min-w-0 block max-w-full',
            !replaceClassColor && classColours[character.characterClass].text,
          )}
          style={
            replaceClassColor ? { color: rankColor ?? undefined } : undefined
          }
        >
          {character.name}
        </span>
      ) : (
        <CharacterLink
          character={character}
          className={cn(
            'truncate min-w-0 block max-w-full',
            !replaceClassColor && classColours[character.characterClass].text,
          )}
          style={
            replaceClassColor ? { color: rankColor ?? undefined } : undefined
          }
        >
          {character.name}
        </CharacterLink>
      )}
    </div>
  )
}

export default TeamMemberLink
